/**
 * Doomsworn — the Pact Ledger
 *
 * `pacts.ts` holds the maths of a single Reckoning. This is the book it is
 * written into: per-Power Debt that persists across sessions and campaigns,
 * the Liens a Power takes when you cannot pay, the Callings it issues when the
 * tally crosses a threshold, and what its Appetite does on the world tick
 * whether or not anyone is watching.
 *
 * Engine authority (ISS-0132): every function here is pure and returns new
 * state. The narrator is handed a `report` string and a set of reads. It has
 * no path to a write. Debt cannot be forgiven in narration, a Lien cannot be
 * described away, and a Calling cannot be quietly dropped because the scene
 * would flow better without it.
 *
 * Determinism: ids come off a monotonic `seq` on the ledger and timestamps are
 * passed in as `at`. Nothing here reads the clock or the global RNG, so a
 * campaign replays identically from the same seed.
 */
import { applyDrawToTilt, ascendantSide } from './tilt.js';
import { crossedCallingThreshold, resolveReckoning } from './pacts.js';
export function openLedger(characterId, entries = []) {
    return { characterId, entries: entries.map(cloneEntry), seq: entries.length };
}
function cloneEntry(entry) {
    return {
        ...entry,
        liens: [...entry.liens],
        callings: [...entry.callings],
    };
}
function blankEntry(powerId) {
    return { powerId, debt: 0, liens: [], callings: [] };
}
/** The standing with one Power. A Power never drawn on reads as a zero entry, not as absent. */
export function entryFor(ledger, powerId) {
    return ledger.entries.find(e => e.powerId === powerId) ?? blankEntry(powerId);
}
export function debtTo(ledger, powerId) {
    return entryFor(ledger, powerId).debt;
}
/** Everything owed, to everyone. The number that says how much of this person is spoken for. */
export function totalDebt(ledger) {
    return ledger.entries.reduce((sum, e) => sum + e.debt, 0);
}
/** Claims currently standing against the character. The GM plays every one of these. */
export function openLiens(ledger) {
    return ledger.entries.flatMap(e => e.liens.filter(l => !l.satisfied));
}
export function openCallings(ledger) {
    return ledger.entries.flatMap(e => e.callings.filter(c => c.status === 'open'));
}
/**
 * Powers whose Callings were refused. Their existing gifts now act on their own
 * initiative — the GM should be reading this list every scene, not on request.
 */
export function invertedPowers(ledger) {
    return ledger.entries.filter(e => e.inverted).map(e => e.powerId);
}
function upsert(ledger, entry) {
    const i = ledger.entries.findIndex(e => e.powerId === entry.powerId);
    if (i === -1)
        return [...ledger.entries, entry];
    return ledger.entries.map((e, j) => (j === i ? entry : e));
}
/** Where the engine has an opinion and the GM does not get one. */
export function validateDraw(draw) {
    const errors = [];
    const flags = [];
    const cap = draw.power.hungerbound?.maxTier;
    if (cap !== undefined && draw.tier > cap) {
        errors.push(`${draw.power.name} is bound into ${draw.power.hungerbound.vessel} and answers to Tier ${cap} at most — Tier ${draw.tier} is beyond what fits in the vessel.`);
    }
    // Grammar is the GM's call, not the engine's. An unlisted verb is a note for
    // adjudication, never a rejection: the whole design is verb × domain, and a
    // closed verb list would quietly rebuild the spell list this pillar exists
    // to avoid.
    if (draw.power.verbs.length > 0 && !draw.power.verbs.some(v => v.toLowerCase() === draw.verb.toLowerCase())) {
        flags.push(`"${draw.verb}" is outside ${draw.power.name}'s listed grammar (${draw.power.verbs.join(', ')}). Rule on whether it falls inside the domain of ${draw.power.domain} — the engine will charge for it either way.`);
    }
    return { errors, flags };
}
/**
 * Draw on a Power. The effect always happens; this works out what it cost.
 *
 * Order of operations matters and is fixed: roll the Reckoning against Debt as
 * it stood BEFORE this Draw, apply the debt change, then test the threshold on
 * the new total. A Draw can therefore both incur Debt and trigger the Calling
 * that Debt crosses, in that order, in one call.
 */
export function draw(ledger, request) {
    const { errors, flags } = validateDraw(request);
    if (errors.length > 0) {
        throw new Error(`Invalid Draw: ${errors.join(' ')}`);
    }
    const power = request.power;
    const before = entryFor(ledger, power.id);
    const reckoning = resolveReckoning({
        tier: request.tier,
        attunementMod: request.attunementMod,
        debt: before.debt,
        powerSide: power.side,
        tilt: request.tilt,
        rng: request.rng,
    });
    let seq = ledger.seq;
    const wear = [];
    let lien;
    let calling;
    const debtAfter = Math.max(0, before.debt + reckoning.debtDelta);
    const entry = cloneEntry({ ...before, debt: debtAfter });
    if (reckoning.takesLien) {
        lien = {
            id: `LIEN-${power.id}-${seq++}`,
            powerId: power.id,
            claim: request.lienClaim ?? `[unwritten claim by ${power.name} — the GM owes this table a specific thing, taken now]`,
            takenAt: request.at,
            satisfied: false,
        };
        entry.liens = [...entry.liens, lien];
        // A Lien is a claim on the person, so it leaves a mark on the person.
        wear.push({
            id: `WEAR-${power.id}-${seq++}`,
            source: 'lien',
            description: `${power.name} took a claim: ${lien.claim}`,
            takenAt: request.at,
        });
        if (!request.lienClaim) {
            flags.push(`${power.name} has taken a Lien and the claim is unwritten. Name it before the scene moves on — a Lien the table cannot state is a Lien the GM will forget to play.`);
        }
    }
    const threshold = crossedCallingThreshold(before.debt, debtAfter);
    if (threshold !== null) {
        calling = {
            id: `CALL-${power.id}-${seq++}`,
            powerId: power.id,
            ask: request.callingAsk ?? `[unwritten ask by ${power.name} at Debt ${threshold}]`,
            issuedAt: request.at,
            status: 'open',
        };
        entry.callings = [...entry.callings, calling];
        flags.push(`Debt to ${power.name} crossed ${threshold}. It is asking for something. Refusal inverts the character's Attunement toward it and its existing gifts begin acting on their own initiative.`);
    }
    if (reckoning.manifests) {
        flags.push(`${power.name} is on the map and has a turn. It has come to collect in person — it does not negotiate, and it is not here to be fought off.`);
    }
    if (reckoning.volunteersBoon) {
        flags.push(`${power.name} is pleased. Either forgive ${request.tier} Debt or offer a boon with an agenda attached — the agenda is ${power.appetite}, and the boon should serve it.`);
    }
    const next = {
        ...ledger,
        entries: upsert(ledger, entry),
        seq,
    };
    const declaration = `${request.verb} · ${request.object} · T${request.tier} — ${power.name}`;
    return {
        declaration,
        reckoning,
        ledger: next,
        tilt: applyDrawToTilt(request.tilt, power.side, request.tier),
        lien,
        calling,
        wear,
        manifests: reckoning.manifests,
        flags,
        report: [
            declaration,
            reckoning.description,
            `Debt to ${power.name}: ${before.debt} → ${debtAfter}.`,
            `The Tilt moves ${request.tier} toward ${power.side === 'chaos' ? 'Chaos' : 'Law'}.`,
        ].join(' '),
    };
}
// ---------------------------------------------------------------------------
// LIENS
// ---------------------------------------------------------------------------
/**
 * Mark a Lien satisfied. It is never deleted: the record that the Power once
 * held this claim is exactly the kind of thing a later Calling refers back to.
 */
export function satisfyLien(ledger, lienId) {
    return {
        ...ledger,
        entries: ledger.entries.map(e => ({
            ...e,
            liens: e.liens.map(l => (l.id === lienId ? { ...l, satisfied: true } : l)),
        })),
    };
}
// ---------------------------------------------------------------------------
// CALLINGS
// ---------------------------------------------------------------------------
/**
 * Answer a Calling.
 *
 * Answering does NOT clear Debt. Debt is a tally that spends you, not a pool
 * you draw down, and a Power that let you work off what you owe by running its
 * errands would be a quest-giver rather than a creditor. What answering buys
 * is the absence of the inversion — which, at the table, is a great deal.
 */
export function answerCalling(ledger, callingId) {
    return {
        ...ledger,
        entries: ledger.entries.map(e => ({
            ...e,
            callings: e.callings.map(c => c.id === callingId && c.status === 'open' ? { ...c, status: 'answered' } : c),
        })),
    };
}
/**
 * Refuse a Calling.
 *
 * The Power does not retaliate, because retaliation would imply it was owed a
 * favour rather than a debt. It inverts: the Attunement that let the character
 * reach it now lets it reach back, and every gift it has already given starts
 * acting on its own initiative.
 */
export function refuseCalling(ledger, callingId, at) {
    const owner = ledger.entries.find(e => e.callings.some(c => c.id === callingId));
    if (!owner) {
        return { ledger, wear: [], inverted: false, report: 'No such Calling on this ledger.' };
    }
    const calling = owner.callings.find(c => c.id === callingId);
    if (calling.status !== 'open') {
        return { ledger, wear: [], inverted: false, report: `That Calling is already ${calling.status}.` };
    }
    let seq = ledger.seq;
    const wear = [
        {
            id: `WEAR-${owner.powerId}-${seq++}`,
            source: 'calling',
            description: `Refused ${owner.powerId}: "${calling.ask}"`,
            takenAt: at,
        },
    ];
    const entry = {
        ...cloneEntry(owner),
        inverted: true,
        callings: owner.callings.map(c => c.id === callingId ? { ...c, status: 'refused' } : c),
    };
    return {
        ledger: { ...ledger, entries: upsert(ledger, entry), seq },
        wear,
        inverted: true,
        report: `The Calling is refused. Attunement toward this Power is inverted — every gift it has already given now acts on its own initiative, and it does not need to be asked.`,
    };
}
/**
 * Pursue a Power's Appetite for one tick.
 *
 * Intended to be registered as a world-engine tick system once that package
 * settles; kept here and kept pure so it is testable and replayable on its own,
 * and so this pillar does not block on that integration.
 */
export function pursueAppetite(power, debtOwedAcrossTable, tilt, rng = Math.random) {
    const leverage = Math.floor(debtOwedAcrossTable / 10);
    const ascendant = ascendantSide(tilt) === power.side ? 1 : 0;
    const pressure = Math.max(0, Math.min(5, leverage + ascendant));
    const witnessed = rng() < 0.35;
    return {
        powerId: power.id,
        appetite: power.appetite,
        pressure,
        witnessed,
        report: pressure === 0
            ? `${power.name} pursues ${power.appetite} at the edges. Nothing the party would notice yet.`
            : `${power.name} pushes on ${power.appetite} (pressure ${pressure})${witnessed ? ' — and the party is close enough to see it happen.' : ' — out of sight, and it will have already happened when they arrive.'}`,
    };
}
/** Total owed to one Power across every ledger at the table. */
export function tableDebtTo(ledgers, powerId) {
    return ledgers.reduce((sum, l) => sum + debtTo(l, powerId), 0);
}
/** Which side the ledger as a whole leans, by weight of Debt. */
export function ledgerLean(ledger, powers) {
    let law = 0;
    let chaos = 0;
    for (const entry of ledger.entries) {
        const side = powers.find(p => p.id === entry.powerId)?.side;
        if (side === 'law')
            law += entry.debt;
        else if (side === 'chaos')
            chaos += entry.debt;
    }
    if (law === chaos)
        return null;
    return law > chaos ? 'law' : 'chaos';
}
//# sourceMappingURL=ledger.js.map